import {Template} from "../../../../components/template/template.jsx";
import {useParams} from "react-router";
import {FaPlus} from "react-icons/fa";
import Button from "../../../../components/button/button.jsx";
import React, {useEffect, useState} from "react";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {useQuery} from "@tanstack/react-query";
import PassageLabelsService from "../../../../services/web/passage-labels-service.js";
import passageLabelsService from "../../../../services/web/passage-labels-service.js";
import groupService from "../../../../services/web/group-service.js";
import userService from "../../../../services/web/user-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import Swal from "sweetalert2";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import Alert from "../../../../components/alert/alert.jsx";

const types = [
    {label: "Todos", value: "ALL"},
    {label: "Meu agente", value: "USER"},
    {label: "Grupo de operação", value: "GROUPS"},
]

const passageLabel = {
    name: "",
    visibility: {type: "ALL", value: null}
}


function translateError(errMessage, defaultMessage) {
    const i18n = {
        "user.cannot.upsert.passage.labels": "Usuário sem permissão.",
        "user.cannot.update.this.passage.label": "Usuário sem permissão.",
        "passage.label.must.exists.to.update": "Marcador de passagem não encontrado.",
        "passage.label.has.not.changed": "Marcador de passagem sem alteração.",
        "passage.label.requires.name": "O campo \"Nome\" é obrigatório.",
        "passage.label.requires.visibility": "O campo \"Visibilidade\" é obrigatório.",
        "passage.label.group.visibility.requires.group": "O campo \"Grupo de operação\" é obrigatório.",
        "user.cannot.delete.passage.label": "Usuário sem permissão para excluir.",
        "passage.label.must.exists.to.delete": "Marcador de passagem não encontrado.",
        "passage.label.still.in.use": "Marcador de passagem em uso.",
        "passage.label.name.already.in.use": "Este \"nome\" já está sendo utilizado.",
    }

    return i18n[errMessage] ?? defaultMessage;
}
const PassageLabelsForm = () => {
    const {id} = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState(passageLabel);
    const navigate = navigateHook();

    const {data: groups = [], isLoading: isLoadingGroups} = useQuery({
        queryFn: () => groupService.getOptionsMyUser("occurrence"),
        queryKey: ["groups"]
    })

    const {data: currentUser = {}, isLoading: isLoadingCurrentUser} = useQuery({
        queryFn: () => userService.getUserLogged(),
        queryKey: ["currentUser"]
    })

    const {data: label = null, isLoading: isLoadingLabel} = useQuery({
        queryKey: ['entity', id],
        queryFn: () => passageLabelsService.getById(id),
        enabled: !!id
    });

    const {data: isPassageLabelInUse, isLoading: isLoadingIsPassageLabelInUse} = useQuery({
        queryFn: () => passageLabelsService.isPassageLabelInUse(id),
        queryKey: ['isPassageLabelInUse'],
        enabled: !!id
    })

    useEffect(() => {
        if(!label) return;

        let mappedValues = null
        if(label.visibility.type === "GROUPS") {
            mappedValues = {}
            mappedValues.type = label.visibility.type;
            mappedValues.value = label.visibility.value.map(el => Number(el));
        }

        setData({
            name: label.name,
            visibility: mappedValues ? mappedValues :label.visibility,
        })
    }, [label])

    const save = async () => {
        setIsLoading(true);
        let visibilityValue = data.visibility.value;

        if (data.visibility.type === "ALL") {
            visibilityValue = null;
        }

        if (data.visibility.type === "USER") {
            visibilityValue = String(currentUser.id);
        }

        if (data.visibility.type === "GROUPS") {
            visibilityValue = Array.isArray(visibilityValue) ? visibilityValue : [];
        }

        const model = {
            ...data,
            visibility: {
                type: data.visibility.type,
                value: visibilityValue
            }
        };

        if(id){
            model.id = id;
        }

        try{
            const response = await PassageLabelsService.save(model)
            projectUtils.success(!id)
            if(!id){
                navigate(`/passage-labels/form/${response.id}`, false);
            }
        }
        catch (err) {
            const message =
                err?.response?.data?.message ?? ""

            if(message === 'passage.label.has.not.changed'){
                return;
            }

            projectUtils.swalErrorMessage("", translateError(message, "Erro inesperado"), "Ok");
        }
        finally {
            setIsLoading(false)
        }
    }

    const destroy = () => {
        Swal.fire({
            title: "Excluir",
            text: `Excluir o marcador de passagem "${data.name}"?`,
            icon: "warning",
            confirmButtonText: "Sim",
            confirmButtonColor: "#063A6C",
            cancelButtonText: "Não",
            cancelButtonColor: "#c10007",
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                passageLabelsService.delete(id)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso")
                        navigate("/passage-labels")
                    })
                    .catch(error => {
                        console.error(error);
                        let message = '';
                        if (error.response.status !== 500) {
                            message = error.response.data.message;
                        }

                        projectUtils.swalErrorMessage('', translateError(message, "Erro inesperado"), 'Ok');
                    });
            }
        });
    }

    return(
        <Template isLoading={isLoading || isLoadingGroups || isLoadingLabel || isLoadingCurrentUser || isLoadingIsPassageLabelInUse}>
            <Template.Header>
                <Template.Title>Marcador de passagem</Template.Title>
                { !!id &&
                    <Button target="outline-primary" onClick={() => {
                        setData(passageLabel)
                        navigate("/passage-labels/form", false)
                    }}><FaPlus/>Adicionar marcador</Button>
                }
            </Template.Header>
            <Template.Body>
                <Card>
                    <Card.Body>
                       <div className="grid grid-cols-12 gap-4">
                           <div className="col-span-6">
                               <InputForm
                                   id="name"
                                   label="Nome *"
                                   value={data.name}
                                   onInput={({target}) => setData(prev => ({...prev, name: target.value.toUpperCase()}))}
                               />
                           </div>
                           <div className={data.visibility.type === "GROUPS" ? "col-span-3" : "col-span-6"}>
                               <SelectComponent
                                   id="type"
                                   options={types}
                                   value={types.find(el => el.value === data.visibility.type) ?? ""}
                                   label="Visibilidade"
                                   onChange={(target) => {
                                       const newType = target.value;

                                       let newValue = null;

                                       if (newType === "GROUPS") {
                                           newValue = [];
                                       }

                                       if (newType === "USER") {
                                           newValue = null;
                                       }

                                       if (newType === "ALL") {
                                           newValue = null;
                                       }

                                       setData(prev => ({
                                           ...prev,
                                           visibility: {
                                               type: newType,
                                               value: newValue
                                           }
                                       }));
                                   }}
                               />
                           </div>
                           { data.visibility.type === "GROUPS" &&
                               <div className="col-span-3">
                                   <SelectComponent
                                       id="group"
                                       isMulti
                                       options={groups ?? []}
                                       value={
                                           groups.filter(group =>
                                               data.visibility.value?.includes(group.value)
                                           )
                                       }
                                       label="Grupo de operação"
                                       onChange={(selected) =>
                                           setData(prev => ({
                                               ...prev,
                                               visibility: {
                                                   ...prev.visibility,
                                                   value: selected ? selected.map(el => el.value) : []
                                               }
                                           }))
                                       }
                                   />
                               </div>
                           }
                       </div>
                    </Card.Body>
                </Card>
                <Card className="mt-2">
                    <Card.Body className="flex justify-end items-center gap-2">
                        {!!id &&
                          <div className="flex justify-end gap-2 w-full items-center">
                              {isPassageLabelInUse &&
                                <Alert target="warning">Não é possível excluir, pois existem passagens associadas ao marcador.</Alert>
                              }
                              <Button target={"danger"} onClick={destroy} disabled={isPassageLabelInUse} actionFeedbackType='delete'>

                                  Excluir
                              </Button>
                          </div>
                        }
                        <Button onClick={save} actionFeedbackType='save'>Salvar</Button>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}

export default PassageLabelsForm;